/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      // B6-N2 (umbra_search_v2): the one-panel search. Every addition below runs only with this class, so the default is unchanged.
      this.ps2 = this.classList.contains('umbra-ps2');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      // W2-T6: Escape from a result hands focus back to the field; that focus must not reopen the results it just closed.
      if (this.suppressFocusOpen) return;
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // W2-T6: Escape anywhere inside the search (a suggestion, a result tab, a product link) closes the results and puts
      // focus back in the field; before, focus fell to the top of the page because the results became hidden under it. Runs
      // before the tab-button early return below, which used to swallow Escape on the tabs.
      if (evt.key === 'Escape') {
        if (this.contains(document.activeElement) && document.activeElement !== this.input) {
          this.suppressFocusOpen = true;
          this.input.focus();
          this.suppressFocusOpen = false;
        }
        this.close();
        return;
      }

      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      // B6-N2: closing from inside the panel (the close button is a plain button, so Enter and Space reach its click).
      if (this.ps2 && evt.target.closest && evt.target.closest('.js-ps2-close')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      if (this.ps2) {
        this.ps2Nav(key);
        return;
      }
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector(this.ps2
        ? '[aria-selected="true"] > .js-search-link, [aria-selected="true"] .js-prod-link'
        : '[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();
      if (this.ps2) this.setAttribute('loading', '');

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      // R1103B (q5-blog, Mete: "i just dont want to see them under products or collections"): no blog articles in the panel.
      searchParams += `&${encodeURIComponent('resources[type]')}=query,product,collection,page`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        if (this.abortController) {
          this.abortController.abort();
          this.abortController = null;
        }
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      // Abort any earlier request still in flight so stale answers never render.
      if (this.abortController) this.abortController.abort();
      const controller = new AbortController();
      this.abortController = controller;

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`, {
          signal: controller.signal
        });
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        if (!resultsEl) throw new Error('missing predictive search section');
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        // A newer request replaced this one: it owns the UI now.
        if (controller.signal.aborted) return;

        // Throttled (429), server error, network failure or bad markup: treat as
        // "no suggestions", show the normal state and let the next keystroke retry.
        this.statusEl.textContent = '';
        this.removeAttribute('results');
        this.results.innerHTML = '';
        this.close();
      } finally {
        if (this.abortController === controller) this.abortController = null;
        if (this.ps2 && !this.abortController) this.removeAttribute('loading');
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      if (this.ps2) this.removeAttribute('loading');
      this.open();
      if (this.ps2) this.ps2Fit();
      /* B6R-m3: the card review counts (umbra-card-review-count.js). The file is fetched on the first results, never on page
         load, and restarted on every later render. */
      if (this.ps2 && this.dataset.umbraRcSrc) {
        if (!window.umbraCardRc && !document.querySelector('script[data-umbra-rc-js]')) {
          const rc = document.createElement('script');
          rc.src = this.dataset.umbraRcSrc;
          rc.defer = true;
          rc.setAttribute('data-umbra-rc-js', '');
          document.head.appendChild(rc);
        } else {
          document.dispatchEvent(new CustomEvent('umbra:cards'));
        }
      }
    }

    /**
     * B6-N2: arrow keys move through every visible row of the panel in reading order (suggestions, pills, lines, cards,
     * View all); the rows live in several lists, so the sibling walk of the tabbed panel cannot be used.
     * @param {string} key - Key pressed.
     */
    ps2Nav(key) {
      const all = Array.from(this.querySelectorAll('.umbra-ps2-panel .predictive-search__item'))
        .filter((el) => el.offsetParent !== null);
      if (!all.length) return;
      const current = all.findIndex((el) => el.getAttribute('aria-selected') === 'true');
      let next = 0;
      if (key === 'ArrowUp') {
        if (current < 0) return;
        next = current === 0 ? all.length - 1 : current - 1;
      } else if (current >= 0) {
        next = current === all.length - 1 ? 0 : current + 1;
      }
      if (next === current) return;
      if (current >= 0) all[current].setAttribute('aria-selected', 'false');
      all[next].setAttribute('aria-selected', 'true');
      all[next].scrollIntoView({ block: 'nearest' });
      this.statusEl.textContent = '';
      this.setLiveRegionText(all[next].textContent.trim());
    }

    /**
     * B6-N2: on phones the panel ends on the visible viewport (above the iPhone keyboard), so the pinned View all stays
     * reachable. One measure per render and per visual viewport resize; nothing is written in a loop.
     */
    ps2Fit() {
      if (!window.visualViewport || window.matchMedia('(min-width: 770px)').matches) {
        this.style.removeProperty('--ps2-max');
        return;
      }
      const panel = this.querySelector('.umbra-ps2-panel');
      if (!panel) return;
      const vv = window.visualViewport;
      const top = panel.getBoundingClientRect().top;
      this.style.setProperty('--ps2-max', `${Math.max(160, Math.round(vv.offsetTop + vv.height - top))}px`);
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      // Shared page freeze (snippets/umbra-scroll-lock.liquid), taken before the body class below so the scrollbar is measured first.
      if (window.UmbraScrollLock) window.UmbraScrollLock.lock('search-results');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);

      if (this.ps2) {
        // Back closes the panel: one history entry per opening, taken back when the panel closes any other way.
        this.ps2PopHandler = this.ps2PopHandler || (() => {
          this.ps2History = false;
          this.close();
        });
        this.ps2ClickHandler = this.ps2ClickHandler || ((evt) => {
          if (!evt.target.closest('.js-ps2-close')) return;
          this.close();
          this.input.blur();
        });
        this.ps2FitHandler = this.ps2FitHandler || this.ps2Fit.bind(this);
        if (!this.ps2History) {
          this.ps2History = true;
          window.history.pushState({ umbraSearch: true }, '');
        }
        window.addEventListener('popstate', this.ps2PopHandler);
        this.results.addEventListener('click', this.ps2ClickHandler);
        if (window.visualViewport) window.visualViewport.addEventListener('resize', this.ps2FitHandler);
      }
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // Abort an in-flight request when the user dismisses the results.
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');
      if (window.UmbraScrollLock) window.UmbraScrollLock.unlock('search-results');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);

      if (this.ps2) {
        this.removeAttribute('loading');
        window.removeEventListener('popstate', this.ps2PopHandler);
        this.results.removeEventListener('click', this.ps2ClickHandler);
        if (window.visualViewport) window.visualViewport.removeEventListener('resize', this.ps2FitHandler);
        if (this.ps2History) {
          this.ps2History = false;
          window.history.back();
        }
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
